import React from "react";
import { motion } from "framer-motion";
import { DollarSign, Fish, Coffee, GithubIcon, ExternalLinkIcon, TerminalIcon, MessageCircleQuestionMark, Car } from "lucide-react";
const projects = [{
  title: "Zemonie",
  description: "A money management tool that helps you track your income, label your expenses, and visualize your financial data",
  icon: DollarSign,
  color: "blue",
  tech: ["React", "Tailwind", "TypeScript", "Hono", "Better-auth"],
  github: "https://github.com/Pet3r1512/Zemonie",
  live: "https://www.zemonie.site/",
  size: "large"
}, {
  title: "DevIniter",
  description: "Pre-built templates for Web Development. Choose from 3 templates including: NextJS, Vite and ViteStack (Vite + TanStack)",
  icon: TerminalIcon,
  color: "emerald",
  tech: ["Node.js", "TypeScript", "CLI", "npm"],
  github: "https://github.com/Pet3r1512/DevIniter",
  live: "https://deviniter.vercel.app/",
  size: "large"
}, {
  title: "LThai Driving School Website",
  description: "An offical website for a Vietnamese driving teacher.",
  icon: Car,
  color: "orange",
  tech: ["React", "TypeScript", "Tailwind", "ShadCN"],
  live: "https://lthaidrivingschool.com.au/",
  size: "small"
}, {
  title: "YuYu Aquarium Shop",
  description: "An official website for an Aquarium shop located in Vietnam. This includes a dashboard for the owner to upload new images.",
  icon: Fish,
  color: "amber",
  tech: ["NextJS", "TypeScript", "Supabase"],
  live: "https://www.thuysinhyuyu.com/",
  size: "normal"
}, {
  title: "Kaohut",
  description: "Interactive quizzes with authentication and saved records.",
  icon: MessageCircleQuestionMark,
  color: "cyan",
  tech: ["Vite", "Tanstack", "Tailwind", "Prisma"],
  github: "https://github.com/Pet3r1512/Kaohut",
  size: "normal"
}, {
  title: "Balan Coffee & Roastery",
  description: "An official website for a coffee shop named Balan Coffee & Roaster.",
  icon: Coffee,
  color: "purple",
  tech: ["NextJS", "TypeScript"],
  size: "normal"
}];
function getSizeClass(size: string) {
  switch (size) {
    case "large":
      return "sm:col-span-2 lg:col-span-2 p-8";
    default:
      return "p-6";
  }
}
function getColorClasses(color: string) {
  const colors: Record<string, string> = {
    emerald: "group-hover:text-emerald-400",
    blue: "group-hover:text-blue-400",
    amber: "group-hover:text-amber-400",
    cyan: "group-hover:text-cyan-400",
    purple: "group-hover:text-purple-400",
    orange: "group-hover:text-orange-500"
  };
  return colors[color] ?? "group-hover:text-emerald-400 bg-slate-700/40";
}
export function ProjectsSection() {
  return <section id="projects" className="py-20 sm:py-28 px-4" data-id="element-274">
      <div className="max-w-6xl mx-auto" data-id="element-275">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center mb-16" data-id="element-276">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-id="element-277">
            Projects
          </h2>
          <div className="w-20 h-1 bg-emerald-500 mx-auto rounded-full" data-id="element-278" />
        </motion.div>

        {/* Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 [grid-auto-flow:dense]" data-id="element-279">
          {projects.map((project, index) => {
          const Icon = project.icon;
          const colorClass = getColorClasses(project.color);
          return <motion.article key={project.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.08
          }} className={`bg-slate-800/50 border border-slate-700 rounded-xl 
                hover:border-slate-600 hover:-translate-y-1 
                transition-all duration-300 group ${getSizeClass(project.size)}`} data-id="element-280">
                {/* Top */}
                <div className="flex items-start justify-between mb-4" data-id="element-281">
                  <div className={`w-12 h-12 bg-slate-700/40 rounded-xl flex items-center justify-center`} data-id="element-282">
                    <Icon className="w-6 h-6 text-white" data-id="element-283" />
                  </div>

                  <div className="flex items-center gap-3" data-id="element-284">
                    {project.github && <a href={project.github} target="_blank" data-id="element-285">
                        <GithubIcon className="w-5 h-5 text-slate-400 hover:text-white transition" data-id="element-286" />
                      </a>}

                    {project.live && <a href={project.live} target="_blank" data-id="element-287">
                        <ExternalLinkIcon className="w-5 h-5 text-slate-400 hover:text-white transition" data-id="element-288" />
                      </a>}
                  </div>
                </div>

                {/* Title */}
                <h3 className={`text-xl font-bold text-white mb-2 transition-colors ${colorClass}`} data-id="element-289">
                  {project.title}
                </h3>

                {/* Description */}
                <p className="text-slate-400 mb-4 text-sm leading-relaxed" data-id="element-290">
                  {project.description}
                </p>

                {/* Tech */}
                <div className="flex flex-wrap gap-2" data-id="element-291">
                  {project.tech.map(tech => <span key={tech} className="px-2.5 py-1 bg-slate-700/50 border border-slate-600 rounded-md text-xs text-slate-300" data-id="element-292">
                      {tech}
                    </span>)}
                </div>
              </motion.article>;
        })}
        </div>
      </div>
    </section>;
}